import React, { useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { Layout, ConfigProvider, App as AntApp } from 'antd';
import { darkTheme, lightTheme, getThemeStyles } from './theme';
import { ThemeProvider, useTheme, useThemeStyles } from './context/ThemeContext';
import { AuthProvider } from './context/AuthContext';
import { useAuth } from './context/AuthContext';
import Navbar from './components/Navbar';
import Sidebar from './components/Sidebar';
import Footer from './components/Footer';
import ServiceAvailabilityBanner from './components/ServiceAvailabilityBanner';
import Landing from './pages/Landing';
import Login from './pages/Login';
import Register from './pages/Register';
import Splash from './pages/Splash';
import CreateProject from './pages/CreateProject';
import ViewProject from './pages/ViewProject';
import ViewProjects from './pages/ViewProjects';
import Chat from './pages/Chat';
import ChatRoom from './pages/ChatRoom';
import Deploy from './pages/Deploy';
import DeployProject from './pages/DeployProject';
import AddDocuments from './pages/AddDocuments';
import Settings from './pages/Settings';
import About from './pages/About';
import AboutV2 from './pages/AboutV2';
import CollectionManager from './pages/CollectionManager';
import UserManagement from './pages/UserManagement';
import CategoryManagement from './pages/CategoryManagement';
import Groups from './pages/Groups';
import RbacProjects from './pages/RbacProjects';
import MyGroups from './pages/MyGroups';
import S3Files from './pages/S3Files';
import { AdminUserBudgetsPage, AdminBudgetRequestsPage, MyUsagePage, AdminBudgetsPage } from './features/budgeting';
import './App.css';

const { Header, Sider, Content } = Layout;

function AppContent() {
  const { theme } = useTheme();
  const themeStyles = useThemeStyles();
  const isLight = theme === 'light';
  const antdTheme = theme === 'light' ? lightTheme : darkTheme;
  const { token, isSuperAdmin, isAuthenticated, isAdmin, isGroupAdmin } = useAuth();

  useEffect(() => {
    const cursorLight = document.createElement('div');
    cursorLight.id = 'cursor-light';
    document.body.appendChild(cursorLight);

    const handleMouseMove = (e) => {
      cursorLight.style.left = `${e.clientX}px`;
      cursorLight.style.top = `${e.clientY}px`;
    };

    document.addEventListener('mousemove', handleMouseMove);

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.body.removeChild(cursorLight);
    };
  }, []);

  return (
    <ConfigProvider theme={antdTheme}>
      <AntApp notification={{ placement: 'topRight' }}>
        <BrowserRouter>
        <Routes>
          <Route path="/login" element={<Login />} />
          <Route path="/register" element={<Register />} />
          <Route path="/splash" element={
            <ConfigProvider theme={darkTheme}>
            <Layout style={{ minHeight: '100vh', display: 'flex', flexDirection: 'column' }}>
              <Header style={{ padding: 0 }}>
                <Navbar showAuth forcePublicDark />
              </Header>
              <Content
                style={{
                  background: getThemeStyles('dark').contentBg,
                  overflow: 'auto',
                  flex: 1,
                  minHeight: 0
                }}
              >
                <Splash />
              </Content>
              <Footer forcePublicDark />
            </Layout>
            </ConfigProvider>
          } />
          <Route path="*" element={
            !isAuthenticated ? <Navigate to="/splash" replace /> :
            <Layout style={{ height: '100vh', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
              <Header
                style={{
                  padding: 0,
                  height: 48,
                  lineHeight: '48px',
                  flexShrink: 0,
                  zIndex: 1000,
                }}
              >
                <Navbar />
              </Header>
              <Layout
                style={{
                  flex: 1,
                  overflow: 'hidden',
                  display: 'flex',
                  padding: 0,
                  minHeight: 0,
                }}
              >
                <Sider
                  width={52}
                  style={{
                    flexShrink: 0,
                    overflow: 'hidden',
                    background: themeStyles.siderBg,
                    borderRight: isLight
                      ? `1px solid ${themeStyles.shellDivider}`
                      : `1px solid ${themeStyles.borderColor}`,
                  }}
                >
                  <Sidebar />
                </Sider>
                <Content
                  style={{
                    background: themeStyles.contentBg,
                    overflow: 'auto',
                    flex: 1,
                    minHeight: 0,
                    ...(isLight
                      ? {
                          boxShadow: '-1px 0 0 0 #E5E7EB, inset 0 1px 0 0 rgba(15, 15, 30, 0.04)',
                        }
                      : {}),
                  }}
                >
                  <ServiceAvailabilityBanner token={token} />
                  <Routes>
                    <Route path="/home" element={<Landing />} />
                    <Route path="/create" element={isSuperAdmin ? <Navigate to="/home" replace /> : <CreateProject />} />
                    <Route path="/projects" element={<ViewProjects />} />
                    <Route path="/project/:projectId" element={<ViewProject />} />
                    <Route path="/project/:projectId/add-documents" element={<AddDocuments />} />
                    <Route path="/chat" element={isSuperAdmin ? <Navigate to="/home" replace /> : <Chat />} />
                    <Route path="/chat/:deploymentId" element={isSuperAdmin ? <Navigate to="/home" replace /> : <ChatRoom />} />
                    <Route path="/deploy" element={isSuperAdmin || !isAdmin ? <Navigate to="/home" replace /> : <Deploy />} />
                    <Route path="/deploy/:projectId" element={isSuperAdmin || !isAdmin ? <Navigate to="/home" replace /> : <DeployProject />} />
                    <Route path="/settings" element={<Settings />} />
                    <Route path="/collections" element={<CollectionManager />} />
                    <Route path="/users" element={<UserManagement />} />
                    <Route path="/categories" element={<CategoryManagement />} />
                    <Route
                      path="/admin/cost"
                      element={<Navigate to="/admin/budgets" replace />}
                    />
                    <Route path="/admin/budgets" element={<AdminBudgetsPage />} />
                    <Route
                      path="/admin/user-budgets"
                      element={isSuperAdmin ? <Navigate to="/admin/budgets" replace /> : <AdminUserBudgetsPage />}
                    />
                    <Route
                      path="/admin/budget-requests"
                      element={isSuperAdmin ? <Navigate to="/admin/budgets" replace /> : <AdminBudgetRequestsPage />}
                    />
                    <Route path="/me/usage" element={isSuperAdmin ? <Navigate to="/home" replace /> : <MyUsagePage />} />
                    <Route path="/groups" element={<Groups />} />
                    <Route path="/my-groups" element={<MyGroups />} />
                    <Route path="/rbac-projects" element={isSuperAdmin ? <Navigate to="/home" replace /> : <RbacProjects />} />
                    <Route path="/s3-files" element={<Navigate to="/document-library" replace />} />
                    <Route path="/document-library" element={<S3Files />} />
                    <Route path="/about" element={<About />} />
                    <Route path="/about/v2" element={<AboutV2 />} />
                    {/* <Route path="/" element={<Navigate to="/home" replace />} /> */}
                    <Route path="*" element={<Navigate to="/splash" replace />} />
                  </Routes>
                </Content>
              </Layout>
              <Footer />
            </Layout>
          } />
        </Routes>
      </BrowserRouter>
      </AntApp>
    </ConfigProvider>
  );
}

function App() {
  return (
    <ThemeProvider>
      <AuthProvider>
        <AppContent />
      </AuthProvider>
    </ThemeProvider>
  );
}

export default App;
